import { Pagination, Stack, Typography, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import SideBar from "./SideBar";
import TopBar from "./TopBar";
import ProductCard from "./ProductCard";
import { useGetProductsQuery } from "../../../Store/Slices/ProductSlice/ProductApi";
import { useParams } from "react-router-dom";
import ProductCardSkeleton from "./ProductCardSkeleton";

export default function Products() {
  const theme = useTheme();
  const { slug } = useParams();
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState({
    sortType: "",
    brand: "",
    category: !slug?.includes("@") && slug !== "all" ? slug : "",
    byTag: slug?.includes("@") ? slug?.split("@")?.[1] : "",
    exist: false,
  });
  // console.log(query);
  // ✅ Build query string using useMemo to trigger re-fetch when query changes
  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page,
      single_data: "false",
      no_of_docs_each_page: "20",
      is_exist: query.exist,
    });

    if (query.sortType) params.append("sort_type", query.sortType);
    if (query.category) params.append("by_category", query.category);
    if (query.brand) params.append("by_brand", query.brand);
    if (query.byTag) params.append("by_tag", query.byTag);

    return `?${params.toString()}`;
  }, [page, query]);

  // ✅ useGetProductsQuery will automatically refetch when queryString changes
  const { data, isLoading, status } = useGetProductsQuery(queryString, {
    refetchOnMountOrArgChange: true,
  });

  const handleQuery = (key, value) => {
    setQuery((prev) => ({ ...prev, [key]: value }));
    setPage(1); // reset to first page when filters change
  };

  // ✅ Handle slug changes
  useEffect(() => {
    if (slug?.includes("@")) {
      setQuery((prev) => ({
        ...prev,
        byTag: slug.split("@")[1],
        category: "",
      }));
    } else {
      setQuery((prev) => ({
        ...prev,
        category: !slug?.includes("@") && slug !== "all" ? slug : "",
        byTag: "",
      }));
    }
  }, [slug]);

  const productsItems = data?.data?.map((e, index) => (
    <ProductCard data={e} key={index} />
  ));

  const productsSkeleton = Array.from({ length: 10 }).map((_, i) => (
    <ProductCardSkeleton key={i} />
  ));

  return (
    <Stack
      direction={{ xs: "column", lg: "row" }}
      sx={{
        direction: "rtl",
        py: "1rem",
        px: { xs: ".2rem", lg: "2rem", xl: "1rem", xxl: "2rem" },
        alignItems: "flex-start",
      }}
    >
      {/* SideBar */}
      <Stack
        sx={{
          width: { xs: "100%", lg: "28%", xlg: "23%", xl: "20%" },
          position: { lg: "sticky" },
          top: { lg: "90px" },
          alignSelf: { lg: "flex-start" },
          maxHeight: { lg: "calc(100vh - 90px)" },
          overflowY: { lg: "auto" },
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        }}
      >
        <SideBar slug={slug} query={query} handleQuery={handleQuery} />
      </Stack>

      {/* Main */}
      <Stack
        sx={{
          width: { xs: "100%", lg: "80%" },
          px: { xs: "1rem", sm: "2rem" },
          pt: { xs: "10rem", sm: "10rem", lg: "0" },
          // height: { lg: "100%" }, // ✅
          maxHeight: { lg: "100%" }, // ✅
          minHeight: 0,
          // overflowY: { lg: "auto" },
          // overflowX: "hidden",
          display: "flex", // ✅
          flexDirection: "column", // ✅
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: theme.palette.primary.main,
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        }}
      >
        {/* Top Bar */}
        <Stack
          sx={{
            width: "100%",
            height: "10vh",
            flexShrink: 0,
            display: { xs: "none", lg: "block" },
          }}
        >
          <TopBar query={query} handleQuery={handleQuery} />
        </Stack>

        {/* Product list */}
        <Stack
          direction={"row"}
          sx={{
            width: "100%",
            flexWrap: "wrap",
            gap: { xs: "1rem", sm: "2rem" },
            rowGap: "2rem",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {isLoading || status !== "fulfilled" ? (
            productsSkeleton
          ) : productsItems?.length > 0 ? (
            productsItems
          ) : (
            <Typography
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "300px",
              }}
            >
              محصولی یافت نشد
            </Typography>
          )}
        </Stack>

        {/* Pagination */}
        <Stack sx={{ alignItems: "center", mt: "3rem", flexShrink: 0 }}>
          <Pagination
            dir="ltr"
            count={data?.num_of_pages || 1}
            page={page}
            onChange={(e, v) => setPage(v)}
            variant="outlined"
            shape="rounded"
            siblingCount={0}
            boundaryCount={2}
            sx={{
              display: data?.num_of_pages ? "flex" : "none",
              direction: "ltr",
              alignItems: "center",
              zIndex: "100",
              "& .MuiPagination-ul": { direction: "ltr" },
              "& .MuiPaginationItem-root": {
                fontSize: "1rem",
                fontWeight: 500,
                borderRadius: "8px",
                direction: "ltr",
                color: theme.palette.primary.main,
                borderColor: theme.palette.primary.main,
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.secondary.main,
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
}
